import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Alert, Spinner 
} from "react-bootstrap";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";


export default function AjoutStock() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const typeDefaut = queryParams.get('type') || 'entree';

    const [produit, setProduit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    
    const [mouvement, setMouvement] = useState({
        type: typeDefaut,
        quantite: 1,
        motif: '',
        reference: ''
    });

    useEffect(() => {
        fetchProduit();
    }, [id]);

    const fetchProduit = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setProduit(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (mouvement.quantite <= 0) {
            setError("La quantité doit être supérieure à 0");
            return;
        }

        if (mouvement.type === 'sortie' && mouvement.quantite > (produit.stock_actuel || 0)) {
            setError(`Stock insuffisant. Stock actuel: ${produit.stock_actuel}`);
            return;
        }

        setSaving(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            
            const response = await fetch(
                `${API_BASE_URL}/mouvements_stock.php`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        id_produit: id,
                        type: mouvement.type,
                        quantite: mouvement.quantite,
                        motif: mouvement.motif,
                        reference: mouvement.reference,
                        id_utilisateur: user?.id || user?.id_utilisateur || 1
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setSuccess("Mouvement enregistré avec succès !");
                setTimeout(() => {
                    navigate(`/stock/produits/${id}`);
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de l'enregistrement");
            }
        } catch (err) {
            setError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement du produit...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!produit) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Produit non trouvé</h6>
                                <p className="mb-0 small">Le produit que vous recherchez n'existe pas</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate('/stock/produits')}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className={`ri-${mouvement.type === 'entree' ? 'add-box-line' : 'subtract-line'} fs-20 text-primary`} style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">
                                        {mouvement.type === 'entree' ? 'Ajouter au stock' : 'Retirer du stock'}
                                    </h4>
                                    <p className="text-muted small mb-0">
                                        {produit.designation} - Réf: {produit.reference}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate(`/stock/produits/${id}`)}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Alertes modernes */}
                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success" onClose={() => setSuccess("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {success}
                        </Alert>
                    )}

                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-information-line me-2"></i>
                                        Produit concerné
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <div className="product-card d-flex align-items-center gap-4 mb-4 p-3 rounded-3" style={{ background: '#f8fafc' }}>
                                        <div className="product-avatar">
                                            <i className="ri-suitcase-2-line"></i>
                                        </div>
                                        <div className="flex-grow-1">
                                            <h5 className="mb-1 fw-bold">{produit.designation}</h5>
                                            <div className="d-flex gap-3 flex-wrap">
                                                <span className="text-muted small">
                                                    <i className="ri-barcode-line me-1"></i>
                                                    Réf: {produit.reference}
                                                </span>
                                                <span className={`fw-semibold ${(produit.stock_actuel || 0) <= (produit.seuil_alerte || 0) ? 'text-warning' : 'text-muted'}`}>
                                                    <i className="ri-stack-line me-1"></i>
                                                    Stock actuel: {produit.stock_actuel || 0} {produit.unite_mesure}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-4">
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Type de mouvement</Form.Label>
                                                    <div className="d-flex gap-4">
                                                        <div 
                                                            className={`type-option ${mouvement.type === 'entree' ? 'active-entree' : ''}`}
                                                            onClick={() => setMouvement({...mouvement, type: 'entree'})}
                                                        >
                                                            <i className="ri-add-box-line text-success"></i>
                                                            <span>Entrée (ajout)</span>
                                                        </div>
                                                        <div 
                                                            className={`type-option ${mouvement.type === 'sortie' ? 'active-sortie' : ''}`}
                                                            onClick={() => setMouvement({...mouvement, type: 'sortie'})}
                                                        >
                                                            <i className="ri-subtract-line text-danger"></i>
                                                            <span>Sortie (retrait)</span>
                                                        </div>
                                                    </div>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Quantité</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="1"
                                                        value={mouvement.quantite}
                                                        onChange={(e) => setMouvement({...mouvement, quantite: parseInt(e.target.value) || 1})}
                                                        required
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Unité</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={produit.unite_mesure}
                                                        disabled
                                                        className="bg-light border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Référence (optionnel)</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={mouvement.reference}
                                                        onChange={(e) => setMouvement({...mouvement, reference: e.target.value})}
                                                        placeholder="N° BL, N° facture, etc."
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Motif / Observations</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={3}
                                                        value={mouvement.motif}
                                                        onChange={(e) => setMouvement({...mouvement, motif: e.target.value})}
                                                        placeholder="Raison du mouvement..."
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <hr />
                                                <div className="d-flex justify-content-end gap-3">
                                                    <Button 
                                                        variant="outline-secondary" 
                                                        onClick={() => navigate(`/stock/produits/${id}`)}
                                                        className="rounded-pill px-4"
                                                    >
                                                        Annuler
                                                    </Button>
                                                    <Button 
                                                        type="submit" 
                                                        variant={mouvement.type === 'entree' ? 'success' : 'warning'}
                                                        disabled={saving}
                                                        className="rounded-pill px-5"
                                                        style={mouvement.type === 'entree' ? { background: '#10b981', border: 'none' } : { background: '#f59e0b', border: 'none' }}
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Enregistrement...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className={`ri-${mouvement.type === 'entree' ? 'add-box-line' : 'subtract-line'} me-2`}></i>
                                                                Confirmer
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>

                            <Card className="border-0 shadow-sm rounded-3 mt-4" style={{ background: '#f8fafc' }}>
                                <Card.Body className="p-4">
                                    <h6 className="mb-3 fw-semibold">
                                        <i className="ri-information-line text-primary me-2" style={{ color: '#21ACCD' }}></i>
                                        Information
                                    </h6>
                                    {mouvement.type === 'entree' ? (
                                        <p className="small text-muted mb-0">
                                            <i className="ri-check-line text-success me-2"></i>
                                            L'ajout de stock augmentera la quantité disponible. 
                                            Cette opération est généralement utilisée pour les réceptions de commandes, 
                                            les retours clients ou les inventaires positifs.
                                        </p>
                                    ) : (
                                        <p className="small text-muted mb-0">
                                            <i className="ri-alert-line text-warning me-2"></i>
                                            Le retrait de stock diminuera la quantité disponible. 
                                            Cette opération est généralement utilisée pour les ventes, 
                                            les pertes ou les inventaires négatifs.
                                        </p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            <style>{`
                .product-avatar {
                    width: 60px;
                    height: 60px;
                    background: linear-gradient(135deg, #e2f3f5, #c8e8eb);
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .product-avatar i {
                    font-size: 28px;
                    color: #21ACCD;
                }
                
                .type-option {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    border: 2px solid #e2e8f0;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    background: white;
                }
                .type-option i {
                    font-size: 1.2rem;
                }
                .type-option:hover {
                    border-color: #21ACCD;
                    background: #f8fafc;
                }
                .type-option.active-entree {
                    border-color: #10b981;
                    background: #ecfdf5;
                }
                .type-option.active-sortie {
                    border-color: #ef4444;
                    background: #fef2f2;
                }
                
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
            `}</style>
        </>
    );
}